import { StructMapData } from 'components/struct-map/struct-map-types';
import {
  AccessType,
  AggregationFamily,
  AisOrderState,
  ArchivalEntityType,
  AuthenticationLevel,
  BucketAggregator,
  CurrentLocation,
  EadNodeLevel,
  GeneralSearchEntityType,
  NotificationType,
  PageKey,
  ProofOfIdentityType,
  ResearchCategory,
  ResearchSheetState,
  ResearchSheetType,
  Sex,
  SortOrder,
  TextLocation,
} from 'enums';

export enum ErrorCode {
  NOT_FOUND = '400',
  FORBIDDEN = '403',
  BAD_REQUEST = '404',
  INTERNAL_ERROR = '500',
}

type errorMap = {
  [key in ErrorCode]: string;
};

export const errorCodeMap: errorMap = {
  // General Error
  400: 'Chyba při zpracování požadavku.',
  403: 'Nemáte oprávnění pracovat s požadovaným objektem.',
  404: 'Položka s vybraným ID nebyla nalezena.',
  500: 'Interní chyba aplikace.',
};

export interface Sort {
  field: string;
  order: SortOrder;
}

export interface Filter {
  field?: string;
  value?: string | string[] | number; // array in case of IDS filter
  operation: string; // todo: might use enum
  ids?: string[];
  filters?: Filter[];
  useFolding?: boolean;
}

export interface Aggregation {
  family: AggregationFamily;
  aggregator: BucketAggregator;
  name?: string;
  field?: string;
  size?: number;
  shardSize?: number;
  showTermDocCountError?: boolean;
  minDocCount?: number;
  shardMinDocCount?: number;
}

export interface Params {
  size?: number;
  offset?: number;
  sort?: Sort[];
  flipDirection?: boolean;
  filters?: Filter[];
  searchAfter?: string;
  include?: string[];
  aggregations?: Aggregation[];
}

export interface Result<T> {
  items?: T[];
  count?: number;
  searchAfter?: string;
  aggregations?: any;
}

export interface FileRef extends DomainObject {
  contentType: string;
  created: string;
  deleted: string;
  name: string;
  opened: boolean;
  path: string;
  size: number;
  storage: string;
  thumbnail?: FileRef;
}

export interface DomainObject {
  id: string;

  [key: string]: any;
}

export interface LabeledObject extends DomainObject {
  label: string;
  index?: number;
}

export interface Address {
  municipality: string;
  street: string;
  zip: string;
}

export type UserInfo = User;

export interface User extends DomainObject {
  aisAuthenticated?: boolean;
  authenticationLevel?: AuthenticationLevel;
  birthDate?: string;
  birthPlace?: string;
  casId?: string;
  casPersonId?: string;
  citizenshipCountryId?: string;
  currentlyUsedSheetId?: string;
  currentlyUsedSheetOrdinalNumber?: string;
  currentlyUsedSheetType?: ResearchSheetType;
  dataBox?: string;
  deliveryAddress?: Address;
  email?: string;
  firstName?: string;
  lastName?: string;
  maidenName?: string;
  permanentResidence?: Address;
  phone?: string;
  sex?: Sex;
  whoIsVerified?: boolean;
}

export interface WhoIsInfo {
  birthDate?: string;
  citizenshipCountryId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  maidenName?: string;
  whoisVerified?: boolean;
  researchSheetDto?: RegistrationResearchSheet;

  sex?: Sex;
}

export interface RegistrationResearchSheet {
  expiration?: string;
  proofOfIdentityNote?: string;
  proofOfIdentityType?: ProofOfIdentityType;
  proofOfIdentityValue?: string;
  remoteAccessAgreement?: boolean;
  researchCategories?: ResearchCategory[];
  researchTopic?: string;
  researchTopicFrom?: string;
  researchTopicTo?: string;
  type?: ResearchSheetType;
  orderDescription?: string;
}

export interface RegistrationForm {
  birthDate?: string;
  birthPlace?: string;
  citizenshipCountryId?: string;
  dataBox?: string;
  deliveryAddress?: Address;
  email?: string;
  firstName?: string;
  lastName?: string;
  maidenName?: string;
  permanentResidence?: Address;
  phone?: string;
  researchSheetDto?: RegistrationResearchSheet;
  sex?: Sex;
}

export interface ArchivalCollection extends DomainObject {
  destroyed?: boolean;
  movedTo?: ArchivalCollection;
  favourite?: boolean;
  accessibility?: string;
  created?: string;
  updated?: string;
  evidenceNumber?: string;
  mark?: string;
  groups?: string[];
  inventoriedQuantity?: string;
  languages?: string[];
  name?: string;
  sumQuantity?: string;
  originators?: Originator[];
  placesOfOrigin?: Originator[];

  processedQuantity?: string;
  thematicDescription?: string;
  timeRange?: string;
  hasChildren?: boolean;
}

export interface ArchivalAidMetadata extends DomainObject {
  favourite?: boolean;
  archivalCollection?: ArchivalCollection;
  authors?: Originator[];
  created?: string;
  evidenceNumber?: string;
  name?: string;
  timeRange?: string;
  eadNode?: EadNode;
  formalTitle?: string;
  type?: string;
  yearOfOrigin?: number;
  hasChildren?: boolean;
  hasEadNode?: boolean;
  publicCopy?: {
    archivalAidMetadata: ArchivalAidMetadata;
    id: string;
    registered: boolean;
    structMap?: StructMapData;
  };
  note?: string;
}

export interface ArchivalUnitReference extends DomainObject {
  archivalCollection: ArchivalCollection;
  currentLocation: CurrentLocation;
  describingArchivalAids: ArchivalAidMetadata[];
  numericId: number;
  publicCopy: ArchivalUnitCopy;
  summary: string;
  favourite?: boolean;
  entityCharacter: 'ANALOG' | 'DIGITAL' | 'HYBRID';
  evidenceTypeName?: string;
  analog?: boolean;
  structuredLink?: string;
  nonStructuredLink?: string;
  storageUnits?: string[];
  timeRange?: string;
  accessType?: AccessType;
  note?: string;
}

export interface EadNodeReference {
  id: string;
}

export interface ArchivalUnitCopy extends DomainObject {
  aipData?: FileRef[];
  aipXml?: FileRef;
  archivalUnitReference: ArchivalUnitReference;
  created: string;
  id: string;
  numericId: number;
  timeRange?: string;
  evidenceTypeName?: string;
  originator?: Originator;
  private: boolean;
  registered: boolean;
  summary?: string;
  structMap?: StructMapData;
  zipFile?: FileRef;
  eadNode: EadNodeReference;
}

export interface OriginatorEvent extends DomainObject {
  begin: string;
  end: string;
  order: number;
  type: string; //enum
  typeLabel: string;
  studyStatusLabel: string;
  studyStatus: string;
  note: string;
}

export interface OriginatorDating extends Record<string, string> {
  date: string;
  method: string; //enum
  methodLabel: string;
}

export interface OriginatorBody {
  address: string;
  briefDescription: string;
  originalLanguages: string[];
  sourceLinks: OriginatorBodySourceLink[];
  biography: string;
  history: string;
  parent: Originator;
  geoObjectTypeLabel: string;
  sourceInfo: string;
  coordBorder: string;
  coordPoint: string;
  corpPurpose: string;
  corpStructure: string;
  foundingNorms: string;
  note: string;
  scopeNorms: string;
}

export interface OriginatorBodySourceLink extends DomainObject {
  name: string;
  url: string;
}

export interface OriginatorIdentifier extends DomainObject {
  value: string;
  validFrom: string;
  validTo: string;
  verified: string;
  typeLabel: string;
}

export interface Originator extends DomainObject {
  address?: string;
  archivalAids?: ArchivalAidMetadata[];
  archivalCollectionsOriginators?: ArchivalCollection[];
  archivalCollectionsPlacesOfOrigin?: ArchivalCollection[];
  archivalUnits?: ArchivalUnitCopy[];
  created?: string;
  datingLabel?: string;
  //description?: string;
  elzaId?: string;
  endDate?: string;
  endMethod?: string;
  endPlace?: Originator;
  endType?: string;
  geoObjectType?: string;
  identifiers: OriginatorIdentifier[];
  nameVariants?: OriginatorName[];
  originDate?: string;
  originMethod?: string;
  originPlace?: Originator;
  originType?: string;
  originatorClass?: string;
  parent?: Originator;
  places?: Originator[];
  preferredName?: OriginatorName;
  resolved?: boolean;
  subClass?: string;
  /**updated types */
  body: OriginatorBody;
  relations: OriginatorRelation[];
  originDating: OriginatorDating;
  endingDating: OriginatorDating;
  events: OriginatorEvent[];
  originatorClassLabel: string;
  subClassLabel: string;
}

export interface OriginatorName extends DomainObject {
  degreeAfter?: string;
  degreeBefore?: string;
  generalSupplement?: string;
  chronologicalSupplement?: string;
  geoSupplement?: string;
  label?: string;
  primaryPart?: string;
  secondaryPart?: string;
  supplementLabel?: string;
  originalLanguages: string[];
  originalLanguagesLabels: string[];
  type: string; // enum
  typeLabel: string;
}

export interface ResearchSheet extends DomainObject {
  acknowledgingAdmin?: User;
  adminNote?: string;
  created?: string;
  expiration?: string;
  extensionRequested?: string;
  generalAgreementUser?: User;
  inspectionAcknowledgement?: FileRef[];
  inspectionRequest?: FileRef[];
  orderDescription?: string;
  ordinalNumber?: string;
  owner?: User;
  proofOfIdentityNote?: string;
  proofOfIdentityType?: ProofOfIdentityType;
  proofOfIdentityValue?: string;
  remoteAccessAgreementUser?: User;
  researchCategories?: ResearchCategory[];
  researchTopic?: string;
  researchTopicFrom?: string;
  researchTopicTo?: string;
  type?: ResearchSheetType;
  state?: ResearchSheetState;
  requestedExtensionTo?: string;
  remoteAccess?: boolean;
}

export interface Country extends DomainObject {
  label: string;
}

export interface Breadcrumb {
  /**
   *
   */
  id?: string;

  /**
   *
   */
  to?: string;

  /**
   *
   */
  title: string;

  /**
   *
   */
  key: PageKey;
}

export interface GeneralSearchValue {
  created?: string;
  entityId?: string;
  id: string;
  label?: string;
  type: GeneralSearchEntityType;
  level: number;
}

export type NewOrderType = 'BASIC' | 'ORIGIN_ORIGINAL' | 'ORIGIN_LTP';

export interface NewOrder {
  archivalAidMetadata?: ArchivalAidMetadata;
  archivalCollection?: ArchivalCollection;
  archivalUnits?: ArchivalUnitReference[];
  admin?: User;
  adminNote?: string;
  create?: string;
  description?: string;
  email?: string;
  fullName?: string;
  ordinalNumber?: string;
  phone?: string;
  requestedForDate?: string;
  researchSheet?: ResearchSheet;
  sentToEsm?: boolean;
  state?: AisOrderState;
  userAcknowledged?: boolean;
  createdByAdmin?: boolean;
  created?: string;
  type?: NewOrderType;
}

export interface AisOrder extends DomainObject, NewOrder {
  filesExpiration?: Date;
  requestedAisIds?: string[];
  files?: string[];
  archivalUnitPaths?: string[];
}

export type PrivateArchivalAidCopy = ArchivalAidMetadata;

export interface PrivateArchivalUnit extends ArchivalUnitCopy {
  aisOrder?: AisOrder;
  expiration?: string;
  requestedExtensionTo?: string;
}

export interface PrivateArchivalAid extends DomainObject {
  aisOrder?: AisOrder;
  expiration?: string;
  ead?: EadNode;
  archivalAidMetadata: ArchivalAidMetadata;
  structMap: StructMapData;
}

export interface ArchivalUnitReservation extends DomainObject {
  aisOrder?: AisOrder;
  archivalUnitReference?: ArchivalUnitReference;
  created?: string;
  expiration?: string;
  requestedExtensionTo?: string;
}

export interface AnalogDataLoan extends DomainObject {
  archivalUnitReference?: ArchivalUnitReference;
  firstLent?: string;
  lastLent?: string;
  returned?: string;
  pendingReservation?: boolean;
}

export interface PrivateCopyExistenceRecord extends DomainObject {
  archivalUnitReference?: ArchivalUnitReference;
  aisOrder?: AisOrder;
  created?: string;
  deleted?: string;
  identifier?: string;
  name?: string;
  type?: 'ARCHIVAL_COLLECTION' | 'ARCHIVAL_AID' | 'ARCHIVAL_UNIT';
}

export interface DigitalDataView extends DomainObject {
  archivalUnitReference?: ArchivalUnitReference;
  privateCopy?: boolean;
  researchRoom?: boolean;
  researchSheet?: ResearchSheet;
  firstViewed?: string;
  lastViewed?: string;
}

export interface FavouriteArchivalEntity {
  added: string;
  archivalEntityType: ArchivalEntityType;
  entityId: string;
  id: string;
  label: string;
  urlLevel?: number;
  eadId?: string;
}

export interface Labeled extends DomainObject {
  label: string;
  index?: number;
}

export interface Notification extends DomainObject {
  content: string;
  created: string;
  deleted: string;
  updated: string;
  formatted: boolean;
  id: string;
  read: boolean;
  recipient: User;
  sender: User;
  subject: string;
  type: NotificationType;
}

export interface Text extends DomainObject {
  location: TextLocation;
  text: string;
}

export interface Relation extends DomainObject {
  label: string;
  originator: Originator;
}

export interface OriginatorReference extends DomainObject {
  elzaId: string;
  id: string;
  resolved: boolean;
  originType?: string;
  originatorClass?: string; //enum
  subClass?: string; //enum
  originatorClassLabel: string;
  subClassLabel: string;
  preferredName: OriginatorName;
}

export interface OriginatorRelation extends DomainObject {
  id: string;
  order: number;
  targetPart: string; // enum?
  targetPartIndex: number;
  type: string; // enum?
  typeLabel: string;
  relatedEntity: OriginatorReference;
}

export interface EvidenceUnit {
  quantity: string;
  unitKind: string;
  unitKindLabel: string;
  imprintImagePhysFacet: string;
  corroboratioPhysFacet: string;
  imprintOrderPhysFacet: string;
  imprintCountPhysFacet: string;
  techniquePhysFacet: string;
  dimensions: string;
  legendPhysFacet: string;
  writing?: string;
}

export interface SpaceOccupied {
  unitKind: string;
  quantity: string;
}

export interface EadNodeChildView {
  baseIdentifier: string;
  childrenCount: string;
  id: string;
  label: string;
  level: string;
  levelLabel: string; // ENUM
  parent: {
    id: string;
  };
  referenceId: string;
  title: string;
}

export interface EadNodeParentView {
  archivalAidMetadata: ArchivalAidMetadata;
  baseIdentifier: string;
  children: EadNodeChildView[];
  childrenCount: number;
  id: string;
  label: string;
  level: string;
  levelLabel: string; // ENUM
  parent: EadNodeParentView;
  referenceId: string;
  title: string;
}

export interface EadNode extends DomainObject {
  parent?: EadNodeParentView;
  altFormAvail?: string;
  accruals?: string;
  accessRestrict?: string;
  acqInfo?: string;
  archivalAidMetadata?: ArchivalAidMetadata;
  arrangement?: string;
  baseIdentifier?: string;
  bibliography?: string;
  children?: EadNode[];
  childrenCount: number;
  container?: string;
  coordinatesX: number;
  cooridantesY: number;
  coordinates: string[];
  custodHist?: string;
  didPhysDesc: string;
  otherfindaid?: string;
  evidenceUnits: EvidenceUnit[];
  spaceOccupied: SpaceOccupied[];
  favourite?: boolean;
  from?: string;
  fromEstimated?: boolean;
  fromTextual?: string;
  formalTitle?: string;
  langMaterial?: string[];
  majorityLangMaterial: string[];
  levelLabel?: string;
  level?: EadNodeLevel;
  scaleMaterialSpec?: string;
  orientationMaterialSpec?: string;
  volumeMaterialSpec?: string;
  materialspec: string;
  order?: number;
  physDesc?: string;
  referenceId?: string;
  referenceLabels?: {
    type: string;
    label: string;
    value: string;
  }[];
  relations: Relation[];
  storageUnits: string[];
  originalsLoc?: string;
  scopeContent?: string;
  title?: string;
  to?: string;
  toEstimated?: boolean;
  toTextual?: string;
  physTech?: string;
  publicCopy?: ArchivalUnitCopy;
  rules?: string;
  colophon?: {
    arrangers?: Originator[];
    originators?: Originator[];
    arrangerBrief?: string;
    findingAidApprovedBy?: string;
    findingAidEditor?: string;
    releaseDatePlace?: string;
    descriptionDate?: string;
    findingAidDate?: string;
  };
}
